import { useMemo } from "react";
import {
  ArrayParam,
  type QueryParamConfigMap,
  StringParam,
  useQueryParam,
  useQueryParams,
  withDefault,
} from "use-query-params";

import { typedObjectEntries } from "common/helpers/utils";
import type { Campaign } from "common/types";

import type { CampaignsSearchParams } from "../types";

const defaultSearchParams: CampaignsSearchParams = {
  status: [],
  accounts: [],
  type: [],
};

const paramsConfig = typedObjectEntries(defaultSearchParams).reduce(
  (accumulator, [key, value]) => {
    accumulator[key] = withDefault(ArrayParam, value);
    return accumulator;
  },
  {} as QueryParamConfigMap,
);

export default function useCampaignsFilters(campaigns: Campaign[]) {
  const [searchText, setSearchText] = useQueryParam(
    "search",
    withDefault(StringParam, ""),
    {
      removeDefaultsFromUrl: true,
      updateType: "replaceIn",
    },
  );

  const [searchParams, setSearchParams] = useQueryParams(paramsConfig, {
    removeDefaultsFromUrl: true,
    updateType: "replaceIn",
  }) as [
    CampaignsSearchParams,
    (searchParams: Partial<CampaignsSearchParams>) => void,
  ];

  const filteredCampaigns = useMemo(
    () =>
      campaigns
        ? campaigns.filter(({ name, state, owners, outreach_type }) => {
            const isMatchingName = searchText
              ? name
                  .trim()
                  .toLowerCase()
                  .includes(searchText.trim().toLowerCase())
              : true;
            const isMatchingStatus = searchParams.status.length
              ? searchParams.status.includes(state)
              : true;
            const isMatchingAccount = searchParams.accounts.length
              ? !!owners.find((owner) => searchParams.accounts.includes(owner))
              : true;
            const isMatchingType = searchParams.type.length
              ? searchParams.type.includes(
                  outreach_type === "drip" ? "inbound" : "outbound",
                )
              : true;

            return (
              isMatchingName &&
              isMatchingStatus &&
              isMatchingAccount &&
              isMatchingType
            );
          })
        : [],
    [campaigns, searchParams, searchText],
  );

  function clearSearchParams() {
    setSearchParams(defaultSearchParams);
  }

  return {
    searchText,
    setSearchText,
    searchParams,
    setSearchParams,
    filteredCampaigns,
    hasSearchParams: Object.values(searchParams).some(
      (value) => !!value.length,
    ),
    clearSearchParams,
  };
}
